import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Settings, Trash2, Rss, Radio } from 'lucide-react'
import { fetchSeriesRssUrl, SERIES_QUERY_KEYS } from '../../api/series'
import type { Series } from '../../types/series'

interface SeriesCardProps {
  series: Series
  onDelete: (id: string) => void
}

function formatNextEpisode(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Not scheduled'
  const date = new Date(dateStr)
  const now = new Date()
  const diff = date.getTime() - now.getTime()

  if (diff < 0) return 'Generating...'
  if (diff < 3600000) return `in ${Math.round(diff / 60000)} min`
  if (diff < 86400000) return `in ${Math.round(diff / 3600000)} hours`
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export function SeriesCard({ series, onDelete }: SeriesCardProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const handleCopyRssUrl = async () => {
    try {
      const { rssUrl } = await queryClient.fetchQuery({
        queryKey: SERIES_QUERY_KEYS.rssUrl(series.id),
        queryFn: () => fetchSeriesRssUrl(series.id),
        staleTime: Infinity,
      })

      await navigator.clipboard.writeText(rssUrl)
    } catch (error) {
      console.error('Failed to copy series RSS URL', error)
    }
  }

  return (
    <div
      onClick={() => navigate(`/series/${series.id}`)}
      className="group flex h-40 cursor-pointer overflow-hidden rounded-2xl border border-black/[0.06] bg-white transition-all hover:border-black/10 hover:shadow-sm"
    >
      {/* Cover Image - full height, square */}
      <div className="aspect-square h-full shrink-0 overflow-hidden">
        {series.coverImageUrl ? (
          <img
            src={series.coverImageUrl}
            alt={`${series.name} cover`}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand-400 to-brand-600">
            <Radio className="h-10 w-10 text-white" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col justify-between p-4">
        <div>
          <div className="mb-1 flex items-start justify-between gap-2">
            <h3 className="truncate text-base font-semibold text-foreground">{series.name}</h3>
            <StatusBadge status={series.status} />
          </div>
          {series.description && (
            <p className="line-clamp-2 text-sm text-foreground/50">{series.description}</p>
          )}
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 text-xs text-foreground/50">
            <span>{series.episodeCount} episode{series.episodeCount !== 1 ? 's' : ''}</span>
            <span>·</span>
            <span>Next: {formatNextEpisode(series.nextEpisodeDueAt)}</span>
          </div>

          {/* Quick actions */}
          <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
            <ActionButton icon={Settings} label="Settings" onClick={() => navigate(`/series/${series.id}`)} />
            <ActionButton icon={Rss} label="RSS" onClick={() => void handleCopyRssUrl()} />
            <ActionButton icon={Trash2} label="Delete" onClick={() => onDelete(series.id)} danger />
          </div>
        </div>
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active: 'bg-success-50 text-success-700',
    paused: 'bg-warning-50 text-warning-700',
    generating: 'bg-info-50 text-info-700',
  }

  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[status] || 'bg-secondary-100 text-secondary-600'}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

function ActionButton({
  icon: Icon,
  label,
  onClick,
  danger = false,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      className={`cursor-pointer rounded-lg p-1.5 transition-colors ${
        danger
          ? 'text-foreground/40 hover:bg-danger-50 hover:text-danger-600'
          : 'text-foreground/40 hover:bg-black/5 hover:text-foreground'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  )
}
